/**
 * Small synthesized sound set (Web Audio API) — no audio files to download.
 */

type SoundName = 'move' | 'capture' | 'check' | 'castle' | 'promote' | 'start' | 'end' | 'illegal' | 'lowTime' | 'win' | 'loss' | 'draw';

class SoundPlayer {
  private ctx: AudioContext | null = null;
  private enabled = true;

  setEnabled(enabled: boolean) {
    this.enabled = enabled;
  }

  play(name: SoundName) {
    if (!this.enabled) return;
    const ctx = this.context();
    if (!ctx) return;

    switch (name) {
      case 'move':
        this.knock(ctx, 0, 1);
        break;
      case 'capture':
        this.knock(ctx, 0, 1.25);
        this.noise(ctx, 0.005, 0.09, 0.22);
        break;
      case 'castle':
        this.knock(ctx, 0, 1);
        this.knock(ctx, 0.09, 0.9);
        break;
      case 'check':
        this.tone(ctx, 0, 880, 0.12, 0.12, 'triangle');
        this.knock(ctx, 0, 1.1);
        break;
      case 'promote':
        this.tone(ctx, 0, 660, 0.08, 0.1, 'triangle');
        this.tone(ctx, 0.08, 990, 0.12, 0.1, 'triangle');
        break;
      case 'start':
        this.tone(ctx, 0, 523, 0.12, 0.08, 'sine');
        this.tone(ctx, 0.1, 784, 0.18, 0.08, 'sine');
        break;
      case 'end':
        this.tone(ctx, 0, 784, 0.16, 0.09, 'sine');
        this.tone(ctx, 0.14, 659, 0.16, 0.09, 'sine');
        this.tone(ctx, 0.28, 523, 0.3, 0.09, 'sine');
        break;
      case 'win':
        // Rising fanfare, ending on a held chord.
        this.tone(ctx, 0, 523, 0.14, 0.09, 'triangle');
        this.tone(ctx, 0.12, 659, 0.14, 0.09, 'triangle');
        this.tone(ctx, 0.24, 784, 0.14, 0.09, 'triangle');
        this.tone(ctx, 0.38, 1047, 0.7, 0.08, 'triangle');
        this.tone(ctx, 0.38, 784, 0.7, 0.05, 'sine');
        this.tone(ctx, 0.38, 659, 0.7, 0.05, 'sine');
        break;
      case 'loss':
        // Slow, falling line.
        this.tone(ctx, 0, 440, 0.28, 0.08, 'sine');
        this.tone(ctx, 0.24, 392, 0.28, 0.08, 'sine');
        this.tone(ctx, 0.48, 330, 0.6, 0.08, 'sine');
        this.tone(ctx, 0.48, 262, 0.6, 0.05, 'sine');
        break;
      case 'draw':
        this.tone(ctx, 0, 523, 0.2, 0.08, 'sine');
        this.tone(ctx, 0.18, 659, 0.2, 0.08, 'sine');
        this.tone(ctx, 0.36, 523, 0.4, 0.07, 'sine');
        break;
      case 'illegal':
        this.tone(ctx, 0, 180, 0.12, 0.08, 'square');
        break;
      case 'lowTime':
        this.tone(ctx, 0, 1200, 0.05, 0.06, 'sine');
        break;
    }
  }

  private context(): AudioContext | null {
    try {
      this.ctx ??= new AudioContext();
      if (this.ctx.state === 'suspended') void this.ctx.resume();
      return this.ctx;
    } catch {
      return null;
    }
  }

  /** Short wooden "knock" — a pitched-down filtered noise burst. */
  private knock(ctx: AudioContext, delay: number, pitch: number) {
    const t = ctx.currentTime + delay;
    const osc = ctx.createOscillator();
    const gain = ctx.createGain();
    osc.type = 'sine';
    osc.frequency.setValueAtTime(260 * pitch, t);
    osc.frequency.exponentialRampToValueAtTime(90 * pitch, t + 0.06);
    gain.gain.setValueAtTime(0.0001, t);
    gain.gain.exponentialRampToValueAtTime(0.5, t + 0.004);
    gain.gain.exponentialRampToValueAtTime(0.0001, t + 0.08);
    osc.connect(gain).connect(ctx.destination);
    osc.start(t);
    osc.stop(t + 0.1);
    this.noise(ctx, delay, 0.03, 0.15);
  }

  private noise(ctx: AudioContext, delay: number, duration: number, volume: number) {
    const t = ctx.currentTime + delay;
    const length = Math.ceil(ctx.sampleRate * duration);
    const buffer = ctx.createBuffer(1, length, ctx.sampleRate);
    const data = buffer.getChannelData(0);
    for (let i = 0; i < length; i++) data[i] = (Math.random() * 2 - 1) * (1 - i / length);
    const src = ctx.createBufferSource();
    src.buffer = buffer;
    const filter = ctx.createBiquadFilter();
    filter.type = 'bandpass';
    filter.frequency.value = 1800;
    const gain = ctx.createGain();
    gain.gain.value = volume;
    src.connect(filter).connect(gain).connect(ctx.destination);
    src.start(t);
  }

  private tone(ctx: AudioContext, delay: number, freq: number, duration: number, volume: number, type: OscillatorType) {
    const t = ctx.currentTime + delay;
    const osc = ctx.createOscillator();
    const gain = ctx.createGain();
    osc.type = type;
    osc.frequency.setValueAtTime(freq, t);
    gain.gain.setValueAtTime(0.0001, t);
    gain.gain.exponentialRampToValueAtTime(volume, t + 0.01);
    gain.gain.exponentialRampToValueAtTime(0.0001, t + duration);
    osc.connect(gain).connect(ctx.destination);
    osc.start(t);
    osc.stop(t + duration + 0.02);
  }
}

export const sound = new SoundPlayer();

/** Pick the sound for a move from chess.js move flags/SAN. */
export function soundForMove(move: { san: string; flags: string; captured?: string; promotion?: string }): SoundName {
  if (move.san.includes('+') || move.san.includes('#')) return 'check';
  if (move.promotion) return 'promote';
  if (move.flags.includes('k') || move.flags.includes('q')) return 'castle';
  if (move.captured) return 'capture';
  return 'move';
}
